/* eslint-disable @typescript-eslint/no-explicit-any */
import { IndexableById, IndexableByName, IndexableByNameAndValue } from './interfaces/indexable.interface';

type orderArray = 'a' | 'd';

export class ArrayUtils {
  static isStringArray(array: any[]): boolean {
    return array.every((element) => typeof element === 'string');
  }

  /**
   *
   * Indexar array de objetos pelo atributo id de cada objeto.
   * Os objetos do array precisam ter o atributo id e não podem estar repetidos
   *
   */
  static indexById<T extends IndexableById>(array: T[]): any {
    if (!array?.length) {
      return;
    }

    return array.reduce(
      (indexed, item) => {
        indexed[item.id] = item;
        return indexed;
      },
      {} as Record<string, T>,
    );
  }

  /**
   * Transformar array em objeto onde a chave é o name do objeto do array e o valor é o próprio objeto
   */
  static indexByName<T extends IndexableByName>(array: T[]): any {
    if (!array?.length) {
      return;
    }

    return array.reduce(
      (indexed, item) => {
        indexed[item.name!] = item;
        return indexed;
      },
      {} as Record<string, T>,
    );
  }

  /**
   * Transformar array em objeto onde a chave é o name do item e o valor é o value do item
   */
  static indexByNameAndValue<T extends IndexableByNameAndValue>(array: T[]): { [key: string]: string } {
    if (!array?.length) {
      return {} as { [key: string]: string };
    }

    return array.reduce(
      (acc, item) => {
        acc[item.name!] = item.value!;
        return acc;
      },
      {} as { [key: string]: string },
    );
  }

  static sortObjectByDate<T extends { date?: Date }>(array: T[], order?: orderArray): T[] {
    if (!array?.length) {
      return [];
    }

    const asc = !order || order === 'a';
    return array.sort((a, b) => {
      if (a.date! < b.date!) {
        return 1 * -asc;
      }
      if (a.date! > b.date!) {
        return 1 * +asc;
      }
      return 0;
    });
  }

  /**
   * Retornar array numérico em ordem ascendente ou descendente
   * @param order Não informe ou informe 'a' para ordenar de forma ascendente, ou informe 'd' para descendente
   */
  static sortNumbers(array: number[], order?: orderArray): number[] {
    if (!array?.length) {
      return [];
    }

    const asc = !order || order === 'a';
    if (asc) {
      return array.sort((a, b) => a - b);
    } else {
      return array.sort((a, b) => b - a);
    }
  }

  /**
   * Copiar array de objetos sem copiar as referências do objeto,
   * evitando que alterações na cópia afetem o array original.
   * Operador de propagação não funciona para estes casos
   */
  static copyOfObjects<T extends object>(array: T[]): T[] {
    return array.map((e) => ({ ...e }));
  }

  /**
   * Retornar um range de numbers com base no início e fim informados
   */
  static rangeNumber(start: number, end: number): number[] {
    const result = [];
    if (start && end && end >= start) {
      for (let i = start; i <= end; i++) {
        result.push(i);
      }
    }
    return result;
  }
}
